/**
 * "What should the difference between these two surfaces be?" — the
 * expectation question, shared verbatim by `RunSurfaceDifferenceDialog` and
 * `RunDeviationAnalysisDialog` so the question can never drift between them
 * (spec: thickness / conformance verification).
 *
 * Progressive disclosure: only the chosen mode's fields are mounted, because
 * the full set is four modes' worth of numbers and showing them all at once
 * asks the user to ignore most of the form. Unmounting (not disabling) also
 * keeps `expectationPayload` honest — a hidden field cannot contribute a
 * stale value to the mutation.
 *
 * Millimetres in the UI, metres on the wire; `expectationPayload` is the only
 * place that converts. Pavement tolerances are quoted in mm, and asking a
 * surveyor for "0.025" invites a factor-of-1000 typo.
 */
import { useEffect, useId } from 'react';
import { useFormState, useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { LayersQuery } from '@/graphql/graphql';
import type { FeatureResult } from '@/hooks/useFeature';
import { EXPECTATION_MODES, preflight, type ExpectationMode } from '@/lib/surfaceExpectation';

import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { NumericField } from '@/components/ui/numeric-field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SurfacePicker } from '@/components/SurfacePicker';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';

type LayerData = LayersQuery['layers'][number];

/** Literal keys, not a `expectation.mode${…}` template: i18next's generated
 *  key union only accepts keys it can see statically, and a template string
 *  fails that check even when every concrete value is a real key. */
const MODE_LABEL_KEYS: Record<ExpectationMode, 'expectation.modeNone' | 'expectation.modeMatch' | 'expectation.modeConstant' | 'expectation.modeSurfaces'> = {
  NONE: 'expectation.modeNone',
  MATCH: 'expectation.modeMatch',
  CONSTANT: 'expectation.modeConstant',
  SURFACES: 'expectation.modeSurfaces',
};

/** `refineExpectation`'s issue tags, translated. Rendered manually (not via
 *  `<FormMessage/>`, whose default body is the raw untranslated tag) — same
 *  reasoning as the `sameLayer` tag in `RunSurfaceDifferenceDialog`. */
function useExpectationErrorText() {
  const { t } = useTranslation('project');
  const map: Record<string, string> = {
    thicknessRequired: t('expectation.errors.thicknessRequired'),
    toleranceRequired: t('expectation.errors.toleranceRequired'),
    surfaceRequired: t('expectation.errors.surfaceRequired'),
    sameSurface: t('expectation.errors.sameSurface'),
    toleranceNegative: t('expectation.errors.toleranceNegative'),
    failAreaPctRange: t('expectation.errors.failAreaPctRange'),
  };
  return (tag: string | undefined): string | null => (tag && map[tag]) || null;
}

/** `preflight`'s warning tags, translated — same reasoning as
 *  `useExpectationErrorText` above (i18next's generated key union rejects a
 *  templated `expectation.warn.${key}` lookup even though every concrete
 *  value is a real key), plus interpolation params a plain string map can't
 *  carry. */
function useExpectationWarningText() {
  const { t } = useTranslation('project');
  const map: Record<string, (params?: Record<string, unknown>) => string> = {
    toleranceTooFine: (params) => t('expectation.warn.toleranceTooFine', params),
  };
  return (key: string, params?: Record<string, unknown>): string | null => map[key]?.(params) ?? null;
}

export interface ExpectationFieldsProps {
  form: UseFormReturn<any>;
  /** `SURFACES` mode is surface-difference only — a deviation run's reference
   *  is already a single surface, and `deviation.py` refuses that mode at its
   *  argument parser. `false` in `RunDeviationAnalysisDialog`. */
  allowSurfaces: boolean;
  layers: LayerData[];
  /** The `analysis.surfaceExpectation` gate, as returned by `useFeature`. */
  gate: Pick<FeatureResult, 'allowed' | 'gate' | 'promptUpgrade'>;
  /** The run's actual (possibly coarsened) grid resolution, in metres, for
   *  `preflight`'s `toleranceTooFine` check — `resolution` in
   *  `RunSurfaceDifferenceDialog`, `stationGridResolution(volumeResolution)`
   *  in `RunDeviationAnalysisDialog` (spec §10.2 step 2's floor). A prop,
   *  not read from `form`, because this component does not own that field —
   *  it belongs to each dialog's own resolution/volume-resolution control. */
  effectiveResolutionM?: number | null;
}

export function ExpectationFields({ form, allowSurfaces, layers, gate, effectiveResolutionM }: ExpectationFieldsProps) {
  const { t } = useTranslation('project');
  const errorText = useExpectationErrorText();
  const warningText = useExpectationWarningText();
  // `useWatch` (a real hook subscribed via `useSyncExternalStore`), not
  // `form.watch('expectMode')`: this component receives `form` as a prop
  // rather than owning the `useForm()` call, and RHF's plain `.watch()`
  // method only reliably re-renders the component that actually calls
  // `useForm()` — a child reading it from a passed-down `form` can see a
  // stale value on the next render even though `form.getValues()` already
  // shows the update (confirmed empirically: the radio group froze on
  // 'NONE' after the first click). `StationVolumesFields` sidesteps this by
  // never touching `form` at all — the owning dialog watches each field
  // itself and passes plain values down; `useWatch` is the equivalent fix
  // for a component that takes the form object directly, per the brief.
  const watchedMode = useWatch({ control: form.control, name: 'expectMode' }) as ExpectationMode;
  // Collapsed to NONE ON SCREEN when the gate is closed, so every disclosure
  // block below stays shut with one guard rather than three.
  const mode = gate.allowed ? watchedMode : 'NONE';
  const modes = EXPECTATION_MODES.filter((m) => m !== 'SURFACES' || allowSurfaces);

  // The effect below is what makes the ABOVE line safe. Collapsing only the
  // DISPLAYED mode (as the first version of this component did) left the
  // underlying form value at whatever it was when the gate closed — e.g.
  // CONSTANT with a blank thickness. `refineExpectation` validates the raw
  // form value, not `mode`, so it kept demanding a thickness that was no
  // longer on screen: Run silently did nothing, with no way to see why or
  // recover short of closing and reopening the dialog (fix round 2, item 1).
  // Writing form state during render is unsafe with the React Compiler on,
  // so this runs in an effect, keyed on the gate actually closing — not on
  // every render — and only when there is something to reset.
  useEffect(() => {
    if (!gate.allowed && watchedMode !== 'NONE') {
      form.setValue('expectMode', 'NONE', { shouldValidate: true });
    }
  }, [gate.allowed, watchedMode, form]);

  // `useFormState`, not `form.formState` read directly, for the same reason
  // `expectMode` uses `useWatch` above: a plain property read on the `form`
  // object doesn't reliably re-render a component that only received `form`
  // as a prop.
  const { errors } = useFormState({ control: form.control });
  const thicknessError = errorText(errors.expectThicknessMm?.message as string | undefined);
  const underError = errorText(errors.underTolMm?.message as string | undefined);
  const overError = errorText(errors.overTolMm?.message as string | undefined);
  const failAreaPctError = errorText(errors.failAreaPct?.message as string | undefined);
  // Anchored to the synthetic `expectSurfaces` path (fix round 1), not
  // `expectUpperLayerId`/`expectLowerLayerId`: those two fields each carry
  // their own unconditional `<FormMessage/>` inside `SurfacePicker`, which
  // renders `error.message` raw. A pair-level tag like 'sameSurface' landing
  // on either `*LayerId` path showed the untranslated token there, right
  // next to this translated sentence.
  const surfaceError = errorText(errors.expectSurfaces?.message as string | undefined);

  // `preflight` is advisory, not validation, but it still needs live values
  // from fields this component doesn't own the `useForm()` call for — same
  // `useWatch` reasoning as `expectMode` above, not `form.watch()`.
  const watchedThicknessMm = useWatch({ control: form.control, name: 'expectThicknessMm' }) as
    | number
    | null
    | undefined;
  const watchedUnderTolMm = useWatch({ control: form.control, name: 'underTolMm' }) as number | null | undefined;
  const watchedOverTolMm = useWatch({ control: form.control, name: 'overTolMm' }) as number | null | undefined;
  const warnings = gate.allowed
    ? preflight({
        mode,
        thicknessMm: watchedThicknessMm,
        underTolMm: watchedUnderTolMm,
        overTolMm: watchedOverTolMm,
        effectiveResolutionM,
      })
    : [];

  // The question's accessible name. `<FormLabel>`'s usual `htmlFor` targets
  // Radix's `role="radiogroup"` `<div>`, and `<label for>` cannot name a div
  // — a screen-reader user would hear four unlabelled radios and never the
  // question itself (fix round 2, item 6). `aria-labelledby` on the group
  // does what `htmlFor` can't here.
  const questionLabelId = useId();

  return (
    <div className='rounded-md border border-slate-200 p-3 dark:border-stone-700 flex flex-col gap-3'>
      <FormField
        control={form.control}
        name='expectMode'
        render={({ field }) => (
          <FormItem>
            <FormLabel
              id={questionLabelId}
              className={gate.allowed ? 'flex items-center gap-2' : 'flex items-center gap-2 opacity-60'}>
              {t('expectation.question')}
              {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
            </FormLabel>
            <FormControl>
              {/* One `<label>` per option wrapping its `RadioGroupItem`
                  directly, not a nested `FormItem`/`FormControl` pair: the
                  latter gave every item an `aria-describedby` pointing at a
                  description/message element that was never rendered for it
                  (four broken references — fix round 2, item 5). This is the
                  same shape `RunGroundClassificationDialog`'s `algorithm`
                  radio group already uses. */}
              <RadioGroup
                onValueChange={(v) => (gate.allowed ? field.onChange(v) : gate.promptUpgrade())}
                value={gate.allowed ? field.value : 'NONE'}
                aria-labelledby={questionLabelId}
                className='gap-1'>
                {modes.map((m) => (
                  <label key={m} className='flex cursor-pointer items-center gap-2'>
                    {/* Never the HTML `disabled` attribute on a gated
                        control (CLAUDE.md): it swallows the click, and the
                        click is what opens the upgrade dialog. Mirrors
                        StationVolumesFields.tsx:176-181. */}
                    <RadioGroupItem
                      value={m}
                      data-testid={`expect-${m}`}
                      aria-disabled={gate.allowed ? undefined : true}
                      data-locked={gate.allowed ? undefined : 'true'}
                      onClick={(e) => {
                        if (!gate.allowed) {
                          e.preventDefault();
                          gate.promptUpgrade();
                        }
                      }}
                    />
                    <span className={`text-sm font-normal${gate.allowed ? '' : ' opacity-60'}`}>
                      {t(MODE_LABEL_KEYS[m])}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </FormControl>
            <FormDescription>{t('expectation.questionHelp')}</FormDescription>
          </FormItem>
        )}
      />

      {mode === 'CONSTANT' && gate.allowed && (
        <FormField
          control={form.control}
          name='expectThicknessMm'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('expectation.thickness')}</FormLabel>
              <FormControl>
                <NumericField
                  value={field.value ?? null}
                  onChange={field.onChange}
                  step='1'
                  min='0'
                  data-testid='expect-thickness'
                />
              </FormControl>
              <FormDescription>{t('expectation.thicknessHelp')}</FormDescription>
              {thicknessError && <p className='text-sm text-destructive'>{thicknessError}</p>}
            </FormItem>
          )}
        />
      )}

      {mode === 'SURFACES' && gate.allowed && (
        <div className='grid grid-cols-2 gap-2'>
          <div data-testid='expect-upper'>
            <SurfacePicker form={form} prefix='expectUpper' layers={layers} label={t('expectation.upper')} />
          </div>
          <div data-testid='expect-lower'>
            <SurfacePicker form={form} prefix='expectLower' layers={layers} label={t('expectation.lower')} />
          </div>
          {surfaceError && <p className='col-span-2 text-sm text-destructive'>{surfaceError}</p>}
        </div>
      )}

      {mode !== 'NONE' && gate.allowed && (
        <>
          <div className='grid grid-cols-2 gap-2'>
            <FormField
              control={form.control}
              name='underTolMm'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('expectation.under')}</FormLabel>
                  <FormControl>
                    <NumericField
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onClear={() => field.onChange(null)}
                      step='1'
                      min='0'
                      data-testid='expect-under-tol'
                    />
                  </FormControl>
                  {underError && <p className='text-sm text-destructive'>{underError}</p>}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='overTolMm'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('expectation.over')}</FormLabel>
                  <FormControl>
                    <NumericField
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onClear={() => field.onChange(null)}
                      step='1'
                      min='0'
                      data-testid='expect-over-tol'
                    />
                  </FormControl>
                  {overError && <p className='text-sm text-destructive'>{overError}</p>}
                </FormItem>
              )}
            />
          </div>
          {/* A blank tolerance is NOT zero. Blank = "measure this side,
              never fail on it"; 0 = "any excess at all is a defect". The
              user cannot be expected to infer that, so say it once for both
              fields rather than repeating it under each box. */}
          <FormDescription>{t('expectation.toleranceBlankHelp')}</FormDescription>
          <FormField
            control={form.control}
            name='failAreaPct'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('expectation.failAreaPct')}</FormLabel>
                <FormControl>
                  <NumericField
                    value={field.value ?? 5}
                    onChange={field.onChange}
                    step='1'
                    min='0'
                    max='100'
                    data-testid='expect-fail-area-pct'
                  />
                </FormControl>
                <FormDescription>{t('expectation.failAreaPctHelp')}</FormDescription>
                {failAreaPctError && <p className='text-sm text-destructive'>{failAreaPctError}</p>}
              </FormItem>
            )}
          />
          {/* Advisory only — `preflight`'s union has no `block` level, and
              nothing here touches the submit button's `disabled` state.
              Zod (`refineExpectation`) owns hard validity; this is
              information about a field this component doesn't even own
              the value of (the run's effective resolution). */}
          {warnings.length > 0 && (
            <ul data-testid='expect-warnings' className='text-xs text-amber-700 dark:text-amber-400'>
              {warnings.map((w) => (
                <li key={w.key}>{warningText(w.key, w.params)}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
